import toast from 'react-hot-toast'

export enum ToastIds {
  AgentUnavailable = 'agent-unavailable',
  IndexingRepositories = 'indexing-repositories',
  ApiForbiddenError = 'api-forbidden-error'
}

interface ToastOptions {
  message: string
  id?: string
  duration?: number
  style?: Record<string, string>
  iconTheme?: Record<string, string>
  loading?: boolean
}

type ToastData = {
  toastId: string
  timerId?: number
}

const TOAST_STYLES = {
  success: {
    borderColor: 'rgb(34, 197, 94)',
    backgroundColor: 'rgb(240, 253, 244)',
    iconColor: 'rgb(34, 197, 94)',
    title: 'Success'
  },
  error: {
    borderColor: 'rgb(239, 68, 68)',
    backgroundColor: 'rgb(254, 242, 242)',
    iconColor: 'rgb(239, 68, 68)',
    title: 'Error'
  },
  info: {
    borderColor: 'rgb(59, 130, 246)',
    backgroundColor: 'rgb(239, 246, 255)',
    iconColor: 'rgb(59, 130, 246)',
    title: 'Info'
  },
  warning: {
    borderColor: 'rgb(245, 158, 11)',
    backgroundColor: 'rgb(255, 251, 235)',
    iconColor: 'rgb(245, 158, 11)',
    title: 'Warning'
  }
} as const

// Dark-theme surfaces for each toast type. Light pastel tints (e.g. red-50)
// are unreadable in dark mode, so we swap to deep tinted surfaces with a
// brighter border accent when the `.dark` class is active on <html>.
const TOAST_STYLES_DARK = {
  success: { borderColor: 'rgb(63, 174, 107)', backgroundColor: 'rgb(21, 39, 26)' },
  error: { borderColor: 'rgb(232, 96, 76)', backgroundColor: 'rgb(45, 24, 22)' },
  info: { borderColor: 'rgb(96, 150, 246)', backgroundColor: 'rgb(23, 33, 48)' },
  warning: { borderColor: 'rgb(233, 162, 59)', backgroundColor: 'rgb(44, 35, 16)' }
} as const

const defaultToastStyle = {
  maxWidth: '400px',
  border: '1px solid',
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  wordBreak: 'break-word',
  color: 'rgba(0, 0, 0, 0.88)'
} as const

export class BetterToast {
  private static _activeToasts = new Map<string, ToastData>()

  private static _toast(
    type: 'success' | 'error' | 'info' | 'warning' | 'loading',
    args: ToastOptions | string
  ) {
    let options: ToastOptions
    if (typeof args === 'string') {
      options = { message: args }
    } else {
      options = args
    }

    const { message, id, duration = 5000, style = {}, ...rest } = options
    const idToUse = id || message + Date.now()

    if (this._activeToasts.has(idToUse)) {
      const { toastId, timerId } = this._activeToasts.get(idToUse) as ToastData
      clearTimeout(timerId)
      const newTimerId = this._setDismissTimer(idToUse, duration)
      this._activeToasts.set(idToUse, { toastId, timerId: newTimerId })
      return idToUse
    }

    const styleType = type === 'loading' ? 'error' : type

    const toastId = toast.custom(
      t => {
        // Resolve the surface at render time so the toast matches the active
        // theme (the `.dark` class is toggled on <html> by the theme setting).
        const isDark =
          typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
        const styleConfig = isDark ? TOAST_STYLES_DARK[styleType] : TOAST_STYLES[styleType]
        return (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'}`}
            style={{
              ...defaultToastStyle,
              color: isDark ? 'rgba(255, 255, 255, 0.92)' : 'rgba(0, 0, 0, 0.88)',
              borderColor: styleConfig.borderColor,
              backgroundColor: styleConfig.backgroundColor,
              ...style
            }}
          >
            <div className='flex items-center gap-3'>
              <div className='flex flex-col'>
                <span className='text-sm text-tokens-neutral-text-primary'>{message}</span>
              </div>
            </div>
          </div>
        )
      },
      {
        duration: duration === Infinity ? Infinity : duration,
        ...rest
      } as any
    )

    const timerId = this._setDismissTimer(idToUse, duration)
    this._activeToasts.set(idToUse, { toastId, timerId })
    return idToUse
  }

  private static _setDismissTimer(id: string, duration: number) {
    let timerId: number | undefined
    if (duration !== Infinity) {
      timerId = setTimeout(() => {
        BetterToast.dismiss(id)
      }, duration) as unknown as number
    }
    return timerId
  }

  public static success(options: ToastOptions | string) {
    return this._toast('success', options)
  }

  public static error(options: ToastOptions | string) {
    return this._toast('error', options)
  }

  public static info(options: ToastOptions | string) {
    return this._toast('info', options)
  }

  public static warning(options: ToastOptions | string) {
    return this._toast('warning', options)
  }

  public static stickyError(options: ToastOptions) {
    return this._toast(options.loading ? 'loading' : 'error', {
      ...options,
      duration: Infinity
    })
  }

  public static dismiss(id: string) {
    if (this._activeToasts.has(id)) {
      const { toastId, timerId } = this._activeToasts.get(id) as ToastData
      clearTimeout(timerId)
      toast.dismiss(toastId)
      this._activeToasts.delete(id)
    }
  }
}
